import React, { useState, useEffect } from "react";
import { useForm, Link, usePage } from "@inertiajs/react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";

interface AdminLoginProps {
  store_name: string;
  store_logo?: string;
  errors?: {
    email?: string;
    password?: string;
    [key: string]: string | undefined;
  };
}

const AdminLogin = ({ store_name, store_logo, errors = {} }: AdminLoginProps) => {
  const { data, setData, post, processing, transform } = useForm({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const { props } = usePage();
  const { flash } = props as { flash?: { notice?: string; alert?: string } };
  const [alertMessage, setAlertMessage] = useState<string>("");

  useEffect(() => {
    if (flash?.alert) {
      setAlertMessage(flash.alert);
    } else {
      setAlertMessage("");
    }
  }, [flash]);

  transform((data) => ({
    admin: {
      email: data.email,
      password: data.password,
    },
  }));

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setData(name as any, value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post("/admins/sign_in");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200 dark:from-neutral-900 dark:to-neutral-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header with Store Logo and Name */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            {store_logo ? (
              <img src={store_logo} alt={store_name} className="h-12 w-auto" />
            ) : (
              <div className="h-12"></div> // Placeholder if no logo
            )}
          </div>
          <h1 className="text-3xl font-bold text-foreground">
            {store_name}
          </h1>
          <p className="text-muted-foreground mt-2">
            Panel de Administración
          </p>
        </div>

        {/* Login Form */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Iniciar Sesión</CardTitle>
            <CardDescription>
              Ingresa tus credenciales para acceder al panel.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {alertMessage && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {alertMessage}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={data.email}
                  onChange={handleInputChange}
                  placeholder="tu@email.com"
                  required
                  disabled={processing}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={data.password}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    required
                    disabled={processing}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={processing}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={processing}>
                {processing ? "Iniciando sesión..." : "Iniciar Sesión"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Back to Store */}
        <div className="text-center mt-6">
          <Link href="/">
            <Button variant="outline" size="sm" className="bg-white/50">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a la Tienda
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;